/**
 * Referral tracking utilities
 *
 * Provides referral code generation, URL building, URL parsing,
 * and cookie management for the affiliate referral system.
 */

import { logger } from '@/lib/logger';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REFERRAL_COOKIE_NAME = 'sw_referral';
const REFERRAL_COOKIE_DAYS = 30;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ReferralCookieData {
  referral_code: string;
  landing_page: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  cookie_set_at: string;
}

// ---------------------------------------------------------------------------
// Referral Code Generation
// ---------------------------------------------------------------------------

/**
 * Generate a URL-safe referral code from a firm name and full name.
 *
 * Examples:
 *   generateReferralCode("Smith & Associates", "John Smith") => "SMITH-JOHN"
 *   generateReferralCode("", "India Fielder") => "INDIA-FIELDER"
 *   generateReferralCode("Coaching By Jane", "Jane Doe") => "COACHING-JANE"
 *
 * The code is upper-cased, limited to alphanumeric + hyphens, and
 * truncated to a maximum of 30 characters.
 */
export function generateReferralCode(firmName: string, fullName: string): string {
  const sanitize = (str: string): string =>
    str
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remove non-alphanumeric except spaces
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join('-');

  // Prefer the first word of the firm name as the prefix
  const firmWords = firmName
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const nameWords = fullName
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  let prefix: string;
  let suffix: string;

  if (firmWords.length > 0) {
    // Use first word of firm + first name
    prefix = firmWords[0];
    suffix = nameWords[0] || '';
  } else {
    // No firm — use first name + last name
    prefix = nameWords[0] || '';
    suffix = nameWords.length > 1 ? nameWords[nameWords.length - 1] : '';
  }

  const code = sanitize([prefix, suffix].filter(Boolean).join(' '));

  // Cap at 30 chars
  return code.slice(0, 30) || 'PARTNER';
}

// ---------------------------------------------------------------------------
// URL Helpers
// ---------------------------------------------------------------------------

/**
 * Build a referral URL with the given code and optional UTM parameters.
 *
 * @param baseUrl  The full origin + path, e.g. "https://diiivorce.com/pricing"
 * @param code     The referral code
 * @param utmParams Optional UTM parameters to append
 */
export function buildReferralUrl(
  baseUrl: string,
  code: string,
  utmParams?: Record<string, string>,
): string {
  const url = new URL(baseUrl);
  url.searchParams.set('ref', code);

  if (utmParams) {
    for (const [key, value] of Object.entries(utmParams)) {
      if (value) {
        url.searchParams.set(key, value);
      }
    }
  }

  return url.toString();
}

/**
 * Extract referral data from a URL string.
 * Looks for `ref` or `partner` query parameters plus standard UTM params.
 *
 * Returns `null` if no referral code is present.
 */
export function parseReferralFromUrl(url: string): ReferralCookieData | null {
  try {
    const parsed = new URL(url);
    const params = parsed.searchParams;

    const referralCode = params.get('ref') || params.get('partner');
    if (!referralCode) return null;

    return {
      referral_code: referralCode,
      landing_page: parsed.pathname,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      cookie_set_at: new Date().toISOString(),
    };
  } catch {
    logger.warn('[referral] Failed to parse URL for referral data:', url);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Cookie Helpers
// ---------------------------------------------------------------------------

/**
 * Set the referral cookie with the given data.
 * Cookie expires after REFERRAL_COOKIE_DAYS (default 30).
 */
export function setReferralCookie(data: ReferralCookieData): void {
  try {
    if (typeof document === 'undefined') return;

    const expires = new Date();
    expires.setDate(expires.getDate() + REFERRAL_COOKIE_DAYS);

    const cookieValue = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${REFERRAL_COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;

    logger.log('[referral] Cookie set for code:', data.referral_code);
  } catch {
    logger.warn('[referral] Failed to set referral cookie');
  }
}

/**
 * Read the referral cookie, returning parsed data or `null`.
 */
export function getReferralCookie(): ReferralCookieData | null {
  try {
    if (typeof document === 'undefined') return null;

    const cookies = document.cookie.split('; ');
    const match = cookies.find((c) => c.startsWith(`${REFERRAL_COOKIE_NAME}=`));
    if (!match) return null;

    const value = match.split('=').slice(1).join('=');
    return JSON.parse(decodeURIComponent(value)) as ReferralCookieData;
  } catch {
    logger.warn('[referral] Failed to read referral cookie');
    return null;
  }
}

/**
 * Clear the referral cookie (e.g., after successful signup attribution).
 */
export function clearReferralCookie(): void {
  try {
    if (typeof document === 'undefined') return;

    document.cookie = `${REFERRAL_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; Secure`;
    logger.log('[referral] Cookie cleared');
  } catch {
    logger.warn('[referral] Failed to clear referral cookie');
  }
}
